import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLojas } from "../../../hooks/useLojas";
import { useBordeiroEvents } from "../../../hooks/useBordeiroEvents";
import { useFornoEvents } from "../../../hooks/useFornoEvents";
import { Loja } from "../../../types/loja";
import { BrazilDate } from "../../../utils/BrazilDate";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import { subscribeDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import {
  DASHBOARD_QUERY_KEY_PREFIXES,
  OPERATIONAL_QUERY_OPTIONS,
} from "../../../constants/operationalQueryOptions";

interface DashboardContextType {
  startDate: string;
  endDate: string;
  selectedLojaId: number | null;
  appliedStartDate: string;
  appliedEndDate: string;
  appliedLojaId: number | null;
  lojas: Loja[];
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setSelectedLojaId: (id: number | null) => void;
  handleApplyFilter: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined
);

const DASHBOARD_SSE_DEBOUNCE_MS = 1_000;

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(BrazilDate.todayISO());
  const [endDate, setEndDate] = useState(BrazilDate.todayISO());
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);

  const [appliedStartDate, setAppliedStartDate] = useState(
    BrazilDate.todayISO()
  );
  const [appliedEndDate, setAppliedEndDate] = useState(BrazilDate.todayISO());
  const [appliedLojaId, setAppliedLojaId] = useState<number | null>(null);

  const { data: lojas = [] } = useLojas();

  useEffect(() => {
    for (const key of DASHBOARD_QUERY_KEY_PREFIXES) {
      queryClient.setQueryDefaults([key], OPERATIONAL_QUERY_OPTIONS);
    }
    return () => {
      for (const key of DASHBOARD_QUERY_KEY_PREFIXES) {
        queryClient.setQueryDefaults([key], { refetchOnWindowFocus: true });
      }
    };
  }, [queryClient]);

  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [producaoSseAvailable, setProducaoSseAvailable] = useState(true);
  const [entregaSseAvailable, setEntregaSseAvailable] = useState(true);
  const [fornoSseAvailable, setFornoSseAvailable] = useState(true);
  const [filaIntegracaoSseAvailable, setFilaIntegracaoSseAvailable] =
    useState(true);
  const sseAvailable =
    producaoSseAvailable &&
    entregaSseAvailable &&
    fornoSseAvailable &&
    filaIntegracaoSseAvailable;

  const invalidateDashboardNow = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["producao-resumo"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["producao-usuarios"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["montagem-resumo"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["montagem-usuarios"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["entrega-resumo"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["entrega-usuarios"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["bordeiro-resumo-dashboard"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["ranking-montagem-fila"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["fila-estatisticas"],
      refetchType: "active",
    });
    queryClient.invalidateQueries({
      queryKey: ["dashboard-origem-pizzas"],
      refetchType: "active",
    });
  }, [queryClient]);

  const dashboardInvalidateDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const invalidateDashboard = useCallback(() => {
    if (dashboardInvalidateDebounceRef.current != null) {
      clearTimeout(dashboardInvalidateDebounceRef.current);
    }
    dashboardInvalidateDebounceRef.current = setTimeout(() => {
      dashboardInvalidateDebounceRef.current = null;
      invalidateDashboardNow();
    }, DASHBOARD_SSE_DEBOUNCE_MS);
  }, [invalidateDashboardNow]);

  useEffect(() => {
    return () => {
      if (dashboardInvalidateDebounceRef.current != null) {
        clearTimeout(dashboardInvalidateDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setLiveUpdatesEnabled(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);

  const sseOptions = { enabled: liveUpdatesEnabled };

  useRankingEvents("producao", appliedLojaId, {
    ...sseOptions,
    queryKeys: RANKING_SSE_QUERY_KEYS.dashboardProducao,
    onAvailabilityChange: setProducaoSseAvailable,
  });
  useRankingEvents("entrega", appliedLojaId, {
    ...sseOptions,
    queryKeys: RANKING_SSE_QUERY_KEYS.dashboardEntrega,
    onAvailabilityChange: setEntregaSseAvailable,
  });
  useFornoEvents(invalidateDashboard, appliedLojaId, {
    ...sseOptions,
    onAvailabilityChange: setFornoSseAvailable,
  });
  useBordeiroEvents(invalidateDashboard, appliedLojaId, undefined, {
    ...sseOptions,
    onAvailabilityChange: setFilaIntegracaoSseAvailable,
  });

  useEffect(() => {
    if (sseAvailable) return;

    invalidateDashboardNow();
    const interval = setInterval(() => {
      invalidateDashboardNow();
    }, 60_000);
    return () => clearInterval(interval);
  }, [sseAvailable, invalidateDashboardNow]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        invalidateDashboardNow();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [invalidateDashboardNow]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      invalidateDashboard();
    });
  }, [invalidateDashboard]);

  const handleApplyFilter = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedLojaId(selectedLojaId);
    queryClient.invalidateQueries({ queryKey: ["producao-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["producao-usuarios"] });
    queryClient.invalidateQueries({ queryKey: ["montagem-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["montagem-usuarios"] });
    queryClient.invalidateQueries({ queryKey: ["entrega-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["entrega-usuarios"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-origem-pizzas"] });
  };

  return (
    <DashboardContext.Provider
      value={{
        startDate,
        endDate,
        selectedLojaId,
        appliedStartDate,
        appliedEndDate,
        appliedLojaId,
        lojas,
        setStartDate,
        setEndDate,
        setSelectedLojaId,
        handleApplyFilter,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within DashboardProvider");
  }
  return context;
};
